import { effect, inject, untracked } from '@angular/core';
import { Router } from '@angular/router';
import Intercom, {
  shutdown as shutdownIntercom,
  update as updateIntercom,
} from '@intercom/messenger-js-sdk';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { OnyxNamePipe, OnyxPhonePipe, OnyxThemeService } from '@onyx/angular';
import { setUser as setSentryUser } from '@sentry/angular';
import { jwtDecode } from 'jwt-decode';
import { Subject } from 'rxjs';
import { INTERCOM_APP_ID } from '../../../../config/config';
import type { BusinessDocuments } from '../../../common/interfaces/documents/business-documents';
import { LanguageService } from '../../../common/services/language.service';
import { getAdminPanelPermissions } from '../../../dashboard/admin-panel/common/enums/admin-panel-permission';
import type { Business } from '../../../dashboard/management-panel/business/common/interfaces/business';
import { getManagementPanelPermissions } from '../../../dashboard/management-panel/common/enums/management-panel-permission';
import { EmployeeRole } from '../../../dashboard/management-panel/employees/common/enums/employee-role';
import type { EngineConfig } from '../../../dashboard/management-panel/engine-config/common/interfaces/engine-config';
import { AUTH_SENSITIVE_KEYS, AuthStorageKey } from '../enums/auth-storage-key';
import type { BusinessModule } from '../enums/business/business-module';
import { BusinessType } from '../enums/business/business-type';
import type { UserRole } from '../enums/user-role';
import { SystemRole } from '../enums/user-role';
import { AuthHelper } from '../helpers/auth.helper';
import type { AuthData } from '../interfaces/auth-data';
import type { Permission } from '../interfaces/permission';
import type { PermissionRoles } from '../interfaces/permission-roles';
import type { User } from '../interfaces/user';

// Contentsquare
declare global {
  interface Window {
    _uxa?: any[];
  }
}

interface AuthState {
  initialized: boolean;
  companyId: string | null;
  token: string | null;
  roles: UserRole[] | null;
  permissions: Permission[] | null;
  user: User | null;
  business: Business | null;
  documents: BusinessDocuments | null;
  engineConfig: EngineConfig | null;
}

const initialState: AuthState = {
  initialized: false,
  companyId: localStorage.getItem(AuthStorageKey.COMPANY_ID),
  token: null,
  roles: null,
  permissions: null,
  user: null,
  business: null,
  documents: null,
  engineConfig: null,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState<AuthState>(initialState),
  withProps(() => ({
    _languageService: inject(LanguageService),
    _namePipe: inject(OnyxNamePipe),
    _phonePipe: inject(OnyxPhonePipe),
    _router: inject(Router),
    _themeService: inject(OnyxThemeService),
  })),
  withProps(() => ({
    _login$: new Subject<void>(),
    _logout$: new Subject<void>(),
  })),
  withProps((store) => ({
    login$: store._login$.asObservable(),
    logout$: store._logout$.asObservable(),
  })),
  withMethods(() => ({
    _getPermissions(roles: UserRole[]): Permission[] {
      const isSystemAdmin = roles.includes(SystemRole.SYSTEM_ADMIN);
      const isAdmin =
        isSystemAdmin || roles.includes(EmployeeRole.BUSINESS_ADMIN);
      const isManager = isAdmin || roles.includes(EmployeeRole.MANAGER);

      const permissionRoles = {
        isSystemAdmin,
        isAdmin,
        isManager,
      } satisfies PermissionRoles;

      const permissions = {
        ...getAdminPanelPermissions(permissionRoles),
        ...getManagementPanelPermissions(permissionRoles),
      } satisfies Record<Permission, boolean>;

      return Object.entries(permissions)
        .filter(([_, hasAccess]) => hasAccess)
        .map(([permission]) => permission as Permission);
    },
  })),
  withMethods((store) => ({
    hasRole(role: UserRole): boolean {
      return store.roles()?.includes(role) ?? false;
    },

    hasPermission(permission: `${Permission}`): boolean {
      return store.permissions()?.includes(permission as Permission) ?? false;
    },

    setCompanyId(companyId: string): void {
      patchState(store, { companyId });
    },

    setRoles(roles: UserRole[]): void {
      patchState(store, { roles });
    },

    setUser(user: User): void {
      patchState(store, { user });
    },

    updateUser(user: Partial<User>): void {
      patchState(store, (state) => {
        if (!state.user) throw new Error('Unable to update user: user is null');
        return { ...state, user: { ...state.user, ...user } };
      });
    },

    setBusiness(business: Business): void {
      patchState(store, { business });
    },

    updateBusiness(business: Partial<Business>): void {
      patchState(store, (state) => {
        if (!state.business) {
          throw new Error('Unable to update business: business is null');
        }
        return { ...state, business: { ...state.business, ...business } };
      });
    },

    setDocuments(documents: BusinessDocuments): void {
      patchState(store, { documents });
    },

    updateDocuments(documents: Partial<BusinessDocuments>): void {
      patchState(store, (state) => {
        if (!state.documents) {
          throw new Error('Unable to update documents: documents is null');
        }
        return { ...state, documents: { ...state.documents, ...documents } };
      });
    },

    setEngineConfig(engineConfig: EngineConfig): void {
      patchState(store, { engineConfig });
    },

    updateEngineConfig(engineConfig: Partial<EngineConfig>): void {
      patchState(store, (state) => {
        if (!state.engineConfig) {
          throw new Error(
            'Unable to update engine config: engine config is null',
          );
        }
        return {
          ...state,
          engineConfig: {
            ...state.engineConfig,
            ...engineConfig,
          },
        };
      });
    },

    initialize(): void {
      patchState(store, { initialized: true });
      if (store.token()) store._login$.next();
    },

    save(token: string, { login }: { login: boolean }): AuthData {
      const data = jwtDecode<AuthData>(token);
      patchState(store, {
        token,
        companyId: data.companyId,
        roles: data.roles,
        permissions: store._getPermissions(data.roles),
        user: {
          ...data.user,
          roles: data.roles.includes(SystemRole.SYSTEM_ADMIN)
            ? [EmployeeRole.BUSINESS_ADMIN]
            : data.user.roles,
        },
        business: data.business
          ? { ...data.business, branches: data.branches ?? [] }
          : null,
        documents: data.documents,
        engineConfig: data.engineConfig,
      });

      if (login) store._login$.next();

      return data;
    },

    logout(): void {
      patchState(store, (state) => ({
        ...initialState,
        initialized: state.initialized,
        companyId: state.companyId,
      }));
      store._logout$.next();

      for (const key of AUTH_SENSITIVE_KEYS) {
        localStorage.removeItem(key);
      }
    },
  })),
  withComputed((store) => ({
    isSystemAdmin: () => store.hasRole(SystemRole.SYSTEM_ADMIN),
    isCarrier: () => store.business()?.type === BusinessType.CARRIER,
    isLogisticOperator: () =>
      store.business()?.type === BusinessType.LOGISTIC_OPERATOR,
    isSender: () => store.business()?.type === BusinessType.SENDER,
    modules: (): BusinessModule[] => {
      const type = store.business()?.type;
      return type ? AuthHelper.getBusinessModules(type) : [];
    },
  })),
  withMethods((store) => ({
    hasModule(module: `${BusinessModule}`): boolean {
      return store.modules().some((available) => available === module);
    },
  })),
  withHooks({
    onInit: (store) => {
      effect(() => {
        const companyId = store.companyId();
        companyId
          ? localStorage.setItem(AuthStorageKey.COMPANY_ID, companyId)
          : localStorage.removeItem(AuthStorageKey.COMPANY_ID);
      });

      effect(() => {
        const user = store.user();
        if (!user) return;

        store._languageService.setLanguage(user.interfaceLanguage);

        /* eslint-disable camelcase */
        Intercom({
          app_id: INTERCOM_APP_ID,
          language_override: store._languageService.getLocale(),
          utm_campaign: sessionStorage.getItem('utm_campaign') ?? undefined,
          utm_content: sessionStorage.getItem('utm_content') ?? undefined,
          utm_medium: sessionStorage.getItem('utm_medium') ?? undefined,
          utm_source: sessionStorage.getItem('utm_source') ?? undefined,
          utm_term: sessionStorage.getItem('utm_term') ?? undefined,
          alignment: 'left',
          vertical_padding: 16,
          horizontal_padding: 64,
          hide_default_launcher: true,
          theme_mode: untracked(store._themeService.theme),
          user_id: user.uuid,
          name: store._namePipe.transform(user),
          email: user.email,
          phone: store._phonePipe.transform(user.phone),
          Role: store.roles()?.[0],
          company: {
            id: store.companyId(),
            name: store.business()?.displayName,
            Type: store.business()?.type,
          },
        });
        setSentryUser({
          id: user.uuid,
          email: user.email,
          username: store._namePipe.transform(user),
          roles: store.roles(),
          companyId: store.companyId(),
        });

        // Contentsquare
        window._uxa ??= [];
        window._uxa.push(['identify', user.uuid]);
        window._uxa.push([
          'addUserProperties',
          {
            uuid: user.uuid,
            email: user.email,
            roles: store.roles()?.join(',') ?? 'null',
            companyId: store.companyId() ?? 'null',
          },
        ]);
      });

      effect(() => {
        const theme = store._themeService.theme();
        if (store.user()) updateIntercom({ theme_mode: theme });
      });

      store.logout$.subscribe(() => {
        shutdownIntercom();
        setSentryUser(null);

        // Contentsquare
        window._uxa ??= [];
        window._uxa.push(['resetIdentity']);
      });
    },
  }),
);

export type AuthStore = InstanceType<typeof AuthStore>;
